import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Flame, ChevronRight, Crown, Users } from "lucide-react";
import { api } from "../lib/api";
import { useBilling } from "../context/BillingContext";

const STREAK_KEY = "vc_streak_count_v1";
const readStreak = () => {
  try { const n = parseInt(localStorage.getItem(STREAK_KEY) || "0", 10); return isNaN(n) ? 0 : n; } catch { return 0; }
};

// Category meta — drives the tiny chip on each pack tile
const PACK_META = {
  "spicy-mode":     { img: "/assets/home/pack-spicy.png",    accent: "#FF2E9A", category: "🔥 CHAOTIC",    badge: "NEW" },
  "first-date":     { img: "/assets/home/pack-fun.png",      accent: "#FF6B47", category: "🌶️ PLAYFUL" },
  "after-dark":     { img: "/assets/home/pack-night.png",   accent: "#9B6BFF", category: "🌙 LATE NIGHT" },
  "deep-talk":      { img: "/assets/home/pack-deep.png",     accent: "#FF2E9A", category: "💜 INTIMATE" },
  "lowkey-toxic":   { img: "/assets/home/pack-redflag.png",  accent: "#FF5252", category: "🚩 UNFILTERED" },
  "we-listen":      { img: null,                              accent: "#FF2E9A", category: "💋 FLIRTY" },
  "unhinged-funny": { img: null,                              accent: "#FFB547", category: "😈 UNHINGED" },
  "do-you-know-me": { img: "/assets/home/pack-redflag.png",  accent: "#FF2E9A", category: "👀 QUIZ" },
  "date-night":     { img: "/assets/home/pack-romantic.png", accent: "#FFB547", category: "🍷 ROMANTIC" },
  "road-trip":      { img: "/assets/home/pack-night.png",   accent: "#9B6BFF", category: "🌙 LATE NIGHT" },
};

export default function HomeView({ user, onRemotelyTap, onTogetherTap, onGroupTap, onSelectPack }) {
  const nav = useNavigate();
  const { entitlements } = useBilling();
  const [packs, setPacks] = useState([]);
  const [streak, setStreak] = useState(readStreak());  // optimistic localStorage value first
  const isPremium = !!(entitlements?.is_premium || entitlements?.has_bundle);

  useEffect(() => { api.listPacks().then(setPacks).catch(() => {}); }, []);

  // Hydrate streak from server (overrides localStorage if server has a higher
  // value, keeps localStorage as a fast-paint fallback).
  useEffect(() => {
    api.streakGet()
      .then((s) => {
        const server = s?.current || 0;
        setStreak(server);
        try { localStorage.setItem(STREAK_KEY, String(server)); } catch { /* noop */ }
      })
      .catch(() => { /* offline — keep localStorage */ });
  }, []);

  const initial = (user?.name || "").trim()[0]?.toUpperCase();

  return (
    <main className="relative z-10 max-w-md mx-auto px-6 pt-5 pb-44">
      {/* Ambient sparkle particles — luxury social-app atmosphere */}
      <SparkleField />

      {/* Top row — LIVE & REAL pill */}
      <div className="flex items-start justify-start gap-2 mb-6" data-testid="home-top-row">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mt-1"
          style={{
            background: "rgba(15, 8, 22, 0.7)",
            border: "1px solid rgba(255, 255, 255, 0.10)",
            backdropFilter: "blur(8px)",
          }}
          data-testid="home-live-pill"
        >
          <motion.span
            className="w-1.5 h-1.5 rounded-full bg-[#FF2E9A]"
            style={{ boxShadow: "0 0 8px #FF2E9A" }}
            animate={{ opacity: [0.4, 1, 0.4], scale: [0.9, 1.15, 0.9] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden
          />
          <span className="text-white text-[10.5px] font-black tracking-[0.20em]" data-testid="home-content-pill">
            15 PACKS · 1,400+ QUESTIONS
            <span aria-hidden style={{ marginLeft: 6 }}>🔥</span>
          </span>
        </div>
      </div>

      {/* ============ HERO — title + 2 avatars + neon question card ============ */}
      <section className="relative mb-10">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1
            className="text-white font-extrabold leading-[0.96] tracking-tight"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.4rem, 10.5vw, 3.1rem)",
              letterSpacing: "-0.04em",
            }}
            data-testid="home-hero-title"
          >
            Good vibes.
            <br />
            <span style={{ color: "#FF2E9A", textShadow: "0 0 36px rgba(255,46,154,0.65), 0 0 80px rgba(255,46,154,0.35)" }}>
              Real&nbsp;connections.
            </span>
          </h1>
          <p
            className="text-[#B8B8CC] mt-4"
            style={{ fontSize: "14px", lineHeight: "1.55", fontWeight: 400 }}
          >
            Spicy questions, honest conversations and deeper connection.{" "}
            <span style={{ color: "#FF2E9A" }}>💕</span>
          </p>
        </motion.div>

        {/* Question card flanked by 2 cinematic avatars */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-7 flex items-center justify-center gap-2"
          data-testid="home-hero-triptych"
        >
          {/* Alex — host (pink ring) */}
          <div className="flex flex-col items-center" style={{ marginRight: -10, zIndex: 2 }}>
            <motion.div
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="w-[88px] h-[88px] rounded-full overflow-hidden"
              style={{
                border: "2.5px solid #FF2E9A",
                boxShadow: "0 0 22px rgba(255, 46, 154, 0.8)",
              }}
            >
              <img src="/assets/home/avatar-pink-w.png" alt="Alex" className="w-full h-full object-cover" />
            </motion.div>
            <div
              className="mt-1.5 px-2.5 py-[3px] rounded-full text-white text-[10px] font-extrabold"
              style={{ background: "rgba(15, 8, 22, 0.85)", border: "1.5px solid #FF2E9A", boxShadow: "0 0 8px rgba(255, 46, 154, 0.5)" }}
            >
              Alex · Host
            </div>
          </div>

          {/* Center neon question card */}
          <motion.button
            onClick={() => onTogetherTap?.()}
            whileTap={{ scale: 0.97 }}
            animate={{ boxShadow: [
              "0 0 32px rgba(255, 46, 154, 0.55), 0 0 70px rgba(255, 46, 154, 0.28)",
              "0 0 50px rgba(255, 46, 154, 0.95), 0 0 100px rgba(255, 46, 154, 0.5)",
              "0 0 32px rgba(255, 46, 154, 0.55), 0 0 70px rgba(255, 46, 154, 0.28)",
            ] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            data-testid="home-hero-reveal-card"
            className="relative rounded-[22px] p-3.5 text-center"
            style={{
              flex: "1 1 0",
              background: "linear-gradient(180deg, rgba(15, 8, 22, 0.96), rgba(8, 4, 12, 0.96))",
              border: "1.5px solid #FF2E9A",
              maxWidth: 200,
              zIndex: 3,
            }}
          >
            <span
              className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-[2px] rounded-full text-[9px] font-black tracking-[0.18em]"
              style={{
                background: "rgba(15, 8, 22, 0.95)",
                border: "1px solid #FF2E9A",
                color: "#FF2E9A",
                boxShadow: "0 0 10px rgba(255, 46, 154, 0.55)",
              }}
            >
              DEEP VIBES
            </span>
            <span className="absolute -top-3 left-3 text-[#FF2E9A] text-base" style={{ filter: "drop-shadow(0 0 6px #FF2E9A)" }}>💗</span>
            <span className="absolute -top-3 right-3 text-[#9B6BFF] text-base" style={{ filter: "drop-shadow(0 0 6px #9B6BFF)" }}>💜</span>
            <p
              className="text-white font-bold mt-1"
              style={{ fontFamily: "'Manrope', sans-serif", fontSize: "12.5px", lineHeight: "1.4" }}
            >
              What's something you've never told your partner?
            </p>
            <div className="text-[10px] font-extrabold mt-2.5" style={{ color: "#FF2E9A" }}>
              Tap to reveal <span style={{ filter: "drop-shadow(0 0 4px #FF2E9A)" }}>💖</span>
            </div>
          </motion.button>

          {/* Jamie — guest (purple ring) */}
          <div className="flex flex-col items-center" style={{ marginLeft: -10, zIndex: 2 }}>
            <motion.div
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.5 }}
              className="w-[88px] h-[88px] rounded-full overflow-hidden"
              style={{
                border: "2.5px solid #9B6BFF",
                boxShadow: "0 0 22px rgba(155, 107, 255, 0.8)",
              }}
            >
              <img src="/assets/home/avatar-blue-m.png" alt="Jamie" className="w-full h-full object-cover" />
            </motion.div>
            <div
              className="mt-1.5 px-2.5 py-[3px] rounded-full text-white text-[10px] font-extrabold"
              style={{ background: "rgba(15, 8, 22, 0.85)", border: "1.5px solid #9B6BFF", boxShadow: "0 0 8px rgba(155, 107, 255, 0.5)" }}
            >
              Jamie · Guest
            </div>
          </div>
        </motion.div>

        {/* Reaction pills row */}
        <div className="flex items-center justify-center gap-1.5 mt-5 flex-wrap" data-testid="home-hero-reactions">
          {[
            { emoji: "😍", n: 12, color: "#FF2E9A" },
            { emoji: "🔥", n: 24, color: "#FF6B1F" },
            { emoji: "👀", n: 8,  color: "#9B6BFF" },
            { emoji: "💀", n: 6,  color: "#C9B7FF" },
            { emoji: "😂", n: 15, color: "#FFD45E" },
          ].map((r, i) => (
            <div
              key={i}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full"
              style={{
                background: "rgba(15, 8, 22, 0.7)",
                border: `1px solid ${r.color}55`,
                boxShadow: `0 0 8px ${r.color}33`,
              }}
            >
              <span className="text-[13px]">{r.emoji}</span>
              <span className="text-white text-[11.5px] font-extrabold">{r.n}</span>
            </div>
          ))}
        </div>

        {/* Activity strip */}
        <div
          className="mt-3 rounded-full px-3 py-2 flex items-center gap-2"
          style={{ background: "rgba(15, 8, 22, 0.65)", border: "1px solid rgba(255, 46, 154, 0.25)" }}
          data-testid="home-hero-activity"
        >
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0" style={{ border: "1.5px solid #FF2E9A" }}>
              <img src="/assets/home/avatar-purple-w.png" alt="" className="w-full h-full object-cover" />
            </div>
            <span className="text-white text-[11px] font-bold truncate">Jamie reacted</span>
            <span style={{ filter: "drop-shadow(0 0 4px #FF6B1F)" }}>🔥</span>
          </div>
          <span className="w-px h-3 bg-white/15" aria-hidden />
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0" style={{ border: "1.5px solid #9B6BFF" }}>
              <img src="/assets/home/avatar-orange-w.png" alt="" className="w-full h-full object-cover" />
            </div>
            <span className="text-white text-[11px] font-bold truncate">Taylor is typing</span>
            <span className="inline-flex gap-0.5">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.18 }}
                  className="w-1 h-1 rounded-full bg-[#FF2E9A]"
                />
              ))}
            </span>
          </div>
        </div>
      </section>

      {/* ============ How are you playing ============ */}
      <section className="mb-7 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Spark />
          <h2
            className="text-white font-bold tracking-tight"
            style={{ fontSize: "22px", letterSpacing: "-0.01em" }}
            data-testid="home-how-title"
          >
            How are you playing tonight?
          </h2>
          <Spark flip />
        </div>
        <p className="text-[14px] text-[#9090A8]" style={{ fontWeight: 400, letterSpacing: "0.005em" }}>Choose the vibe that fits your night.</p>
      </section>

      {/* ============ Mode picker — 3 equal columns ============ */}
      <section className="mb-10" data-testid="home-mode-picker">
        <div className="grid grid-cols-3 gap-2.5" style={{ minHeight: 280 }}>
          <ModeColCard
            accent="#FF2E9A"
            tagline="MOST POPULAR"
            title="Together"
            subtitle="In the same room?"
            ctaLabel="Let's play"
            ctaBg="#FF2E9A"
            imgSrc="/assets/home/mode-together.png"
            testid="mode-together"
            onTap={() => onTogetherTap?.()}
          />
          <ModeColCard
            accent="#7B61FF"
            title="Long distance"
            subtitle="Play from anywhere"
            ctaLabel="Play together"
            ctaBg="#7B61FF"
            imgSrc="/assets/home/mode-remotely.png"
            testid="mode-remotely"
            onTap={onRemotelyTap}
          />
          <ModeColCard
            accent="#FFB800"
            title="Group Mode"
            subtitle="Bring the squad!"
            ctaLabel="Start group"
            ctaBg="linear-gradient(180deg, #FFB800 0%, #FF8A00 100%)"
            ctaText="#1a0e00"
            imgSrc="/assets/home/mode-group.png"
            badge="NEW"
            testid="mode-group"
            onTap={() => (onGroupTap || onRemotelyTap)?.()}
          />
        </div>
      </section>

      {/* ============ Featured Pack spotlight — Red Flag Radar 🚩 ============ */}
      <FeaturedPackSpotlight onSelectPack={onSelectPack} />

      {/* ============ Loved by 500K+ players — social proof card ============ */}
      <button
        onClick={() => onTogetherTap?.()}
        className="float-card w-full mb-8 rounded-[22px] px-4 py-4 flex items-center gap-3.5 text-left"
        style={{
          background: "linear-gradient(135deg, rgba(20, 14, 26, 0.85), rgba(12, 8, 18, 0.85))",
          border: "1px solid rgba(255, 46, 154, 0.28)",
          boxShadow: "0 8px 28px rgba(0, 0, 0, 0.35)",
        }}
        data-testid="home-rating-card"
      >
        {/* Avatar stack */}
        <div className="flex items-center flex-shrink-0">
          {["pink-w", "blue-m", "purple-w", "orange-w"].map((id, i) => (
            <div
              key={id}
              className="w-8 h-8 rounded-full overflow-hidden"
              style={{
                marginLeft: i === 0 ? 0 : -12,
                border: "2px solid #0F0816",
                boxShadow: "0 0 8px rgba(255, 46, 154, 0.45)",
                zIndex: 5 - i,
              }}
            >
              <img src={`/assets/home/avatar-${id}.png`} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-[14px] font-extrabold leading-snug">
            Loved by <span className="text-[#FF2E9A]">500K+ players</span>
          </div>
          <div className="flex items-center gap-1 mt-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <svg key={i} width="13" height="13" viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01z"
                  fill="#FFD45E"
                  style={{ filter: "drop-shadow(0 0 3px rgba(255, 212, 94, 0.7))" }}
                />
              </svg>
            ))}
            <span className="text-white text-[11.5px] font-bold ml-1">4.9 rating</span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-[#9090A8] flex-shrink-0" />
      </button>
    </main>
  );
}

// ============== Mode card — 3-col equal ==============
function ModeColCard({ accent, tagline, title, subtitle, ctaLabel, ctaBg, ctaText = "#fff", imgSrc, badge, testid, onTap }) {
  return (
    <button
      onClick={onTap}
      data-testid={testid}
      className="vc-mode-tile text-left relative overflow-hidden"
      style={{
        background: `radial-gradient(ellipse 130% 80% at 50% 30%, ${accent}33 0%, rgba(8, 4, 14, 0.96) 70%)`,
        border: `1.5px solid ${accent}`,
        boxShadow: `0 0 24px ${accent}50, 0 14px 38px rgba(0, 0, 0, 0.5), inset 0 0 50px ${accent}1a`,
      }}
    >
      {tagline && (
        <span
          className="absolute top-2 left-2 z-10 text-[8px] font-black px-1.5 py-[2px] rounded-md uppercase tracking-[0.10em]"
          style={{
            background: "rgba(0,0,0,0.65)",
            color: accent,
            border: `1px solid ${accent}80`,
            backdropFilter: "blur(6px)",
          }}
        >
          {tagline}
        </span>
      )}
      {badge && (
        <span
          className="absolute top-2 right-2 z-10 text-[8.5px] font-black px-1.5 py-[2px] rounded-md"
          style={{
            background: "#FFD33D",
            color: "#1a0e00",
            letterSpacing: "0.06em",
            boxShadow: "0 0 10px rgba(255, 211, 61, 0.6)",
          }}
        >
          {badge}
        </span>
      )}
      <div className="h-[58%] flex items-center justify-center pt-7 pb-2 px-2">
        <img
          src={imgSrc}
          alt=""
          className="max-w-full max-h-full object-contain"
          style={{ filter: `drop-shadow(0 0 18px ${accent}aa) drop-shadow(0 0 36px ${accent}55)` }}
        />
      </div>
      <div className="px-2.5 pb-2.5">
        <div className="text-white text-[14px] font-extrabold leading-tight tracking-tight">{title}</div>
        <div className="text-[10px] text-[#B8B8CC] mb-2 leading-tight">{subtitle}</div>
        <div
          className="w-full text-center text-[11px] font-bold py-2 rounded-full flex items-center justify-center gap-1"
          style={{
            background: ctaBg,
            color: ctaText,
            boxShadow: `0 0 14px ${accent}90`,
          }}
        >
          {ctaLabel}
          <ChevronRight className="w-3 h-3" strokeWidth={3} />
        </div>
      </div>
    </button>
  );
}

// ============== Removed: ModeHeroCard + ModeSideCard (replaced by ModeColCard above) ==============

// ============== Featured Packs — horizontal carousel with intentional peek ==============
function PacksCarousel({ packs, onSelectPack }) {
  const items = (packs.length ? packs : Array(6).fill(null)).slice(0, 6);
  return (
    <div className="relative -mx-6">
      <div
        className="snap-carousel flex gap-3 px-6 pb-3"
        data-testid="home-packs-carousel"
        style={{ scrollPaddingLeft: 24 }}
      >
        {items.map((p, i) => (
          <PackTile key={p?.id || `s${i}`} pack={p} fallbackIndex={i} onTap={() => p && onSelectPack?.(p)} />
        ))}
        <div className="flex-shrink-0 w-3" aria-hidden />
      </div>
      {/* Right-edge fade — clearly says "more →" instead of "clipped" */}
      <div
        className="absolute right-0 top-0 bottom-3 w-12 pointer-events-none"
        style={{
          background: "linear-gradient(90deg, transparent 0%, #050308 90%)",
        }}
        aria-hidden
      />
    </div>
  );
}

function PackTile({ pack, fallbackIndex, onTap }) {
  const meta = pack ? PACK_META[pack.id] || { accent: "#FF2E9A", category: "🌶️ VIBE" } : { accent: "#FF2E9A", category: "🌶️ VIBE" };
  const isPremium = pack && pack.tier === "premium";
  const isNew = meta.badge === "NEW";
  return (
    <button
      onClick={onTap}
      disabled={!pack}
      data-testid={pack ? `home-pack-${pack.id}` : `home-pack-skeleton-${fallbackIndex}`}
      className="neon-pack-tile relative p-3.5 text-left flex flex-col flex-shrink-0 disabled:opacity-50"
      style={{ width: 158, minHeight: 226 }}
    >
      {isNew && (
        <span
          className="absolute text-[9px] font-black px-2 py-0.5 rounded-md text-white"
          style={{
            background: "#FF2E9A",
            boxShadow: "0 0 12px rgba(255,46,154,0.7)",
            top: 10,
            left: 10,
            letterSpacing: "0.06em",
            zIndex: 2,
          }}
        >
          NEW
        </span>
      )}

      {/* Tier badge — top-right, always visible */}
      {pack && (
        <span
          className="absolute inline-flex items-center gap-1 text-[9px] font-black px-2 py-[3px] rounded-full uppercase"
          style={
            isPremium
              ? {
                  top: 10, right: 10, zIndex: 2,
                  background: "linear-gradient(135deg, #FF2E9A, #FF6B47)",
                  color: "#FFFFFF",
                  border: "1px solid rgba(255, 255, 255, 0.35)",
                  boxShadow: "0 0 14px rgba(255, 46, 154, 0.75)",
                  letterSpacing: "0.08em",
                }
              : {
                  top: 10, right: 10, zIndex: 2,
                  background: "rgba(61, 214, 140, 0.18)",
                  color: "#7DE6A9",
                  border: "1px solid rgba(61, 214, 140, 0.6)",
                  boxShadow: "0 0 10px rgba(61, 214, 140, 0.45)",
                  letterSpacing: "0.08em",
                }
          }
          data-testid={`home-pack-tier-${pack.id}`}
        >
          {isPremium ? "💎 Premium" : "✓ Free"}
        </span>
      )}

      <div className="flex-1 flex items-center justify-center pt-1 pb-1">
        {meta.img ? (
          <img
            src={meta.img}
            alt=""
            className="w-[80px] h-[80px] object-contain"
            style={{ filter: `drop-shadow(0 0 14px ${meta.accent}90)` }}
          />
        ) : (
          <div
            className="w-[80px] h-[80px] flex items-center justify-center text-4xl"
            style={{ filter: `drop-shadow(0 0 14px ${meta.accent}90)`, color: meta.accent }}
          >
            ✦
          </div>
        )}
      </div>
      <div className="mt-1">
        <div
          className="inline-block text-[9px] font-black mb-1.5 px-2 py-0.5 rounded uppercase tracking-[0.12em]"
          style={{
            background: `${meta.accent}1f`,
            color: meta.accent,
            border: `1px solid ${meta.accent}40`,
          }}
        >
          {meta.category}
        </div>
        <div
          className="text-white text-[14px] font-bold leading-tight"
          data-testid={pack ? `home-pack-name-${pack.id}` : undefined}
        >
          {pack?.name || "—"}
        </div>
        <div
          className="text-[#9090A8] line-clamp-2 min-h-[30px]"
          style={{ fontSize: "11px", lineHeight: "1.55", fontWeight: 400, marginTop: 2 }}
        >
          {pack?.description || ""}
        </div>
      </div>
    </button>
  );
}

// ============== Ambient sparkle particles ==============
function SparkleField() {
  // 8 dots with varied positions / delays / durations so the field never feels mechanical.
  const dots = [
    { left: "8%",  delay: "0s",   duration: "11s", bottom: 0 },
    { left: "22%", delay: "3s",   duration: "9s",  bottom: 0 },
    { left: "38%", delay: "6s",   duration: "12s", bottom: 0 },
    { left: "55%", delay: "1.5s", duration: "10s", bottom: 0 },
    { left: "70%", delay: "4.5s", duration: "13s", bottom: 0 },
    { left: "86%", delay: "2s",   duration: "9.5s", bottom: 0 },
    { left: "15%", delay: "7s",   duration: "11.5s", bottom: 0 },
    { left: "65%", delay: "8s",   duration: "10.5s", bottom: 0 },
  ];
  return (
    <div className="sparkle-field" aria-hidden>
      {dots.map((d, i) => (
        <span
          key={i}
          className="sparkle-dot"
          style={{
            left: d.left,
            bottom: d.bottom,
            animationDelay: d.delay,
            animationDuration: d.duration,
          }}
        />
      ))}
    </div>
  );
}

// ============== decorative bits ==============
function Heart16() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF2E9A" style={{ filter: "drop-shadow(0 0 4px #FF2E9A)" }}>
      <path d="M12 21s-7-4.5-7-10a4.5 4.5 0 0 1 8-2.85A4.5 4.5 0 0 1 19 11c0 5.5-7 10-7 10z" />
    </svg>
  );
}

function Spark({ flip = false }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ transform: flip ? "scaleX(-1)" : undefined, filter: "drop-shadow(0 0 5px #FF2E9A)" }}>
      <path d="M4 12h7M5 6l4 4M5 18l4-4" stroke="#FF2E9A" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}

// ============== Red Flag Radar — featured pack spotlight ==============
// Highlights the brand-new premium pack for the first ~30 days post-launch.
// Tapping routes through onSelectPack so the existing paywall preview sheet
// handles the conversion flow (no separate codepath).
function FeaturedPackSpotlight({ onSelectPack }) {
  const handleTap = () => {
    // Mirror the shape the existing onSelectPack handler expects (need id +
    // enough meta for the paywall sheet to render).
    onSelectPack?.({
      id: "red-flag-radar",
      name: "Red Flag Radar",
      emoji: "🚩",
      tier: "premium",
      description: "Are you the red flag, or am I? Brutally honest dating-discourse prompts.",
    });
  };

  return (
    <motion.button
      onClick={handleTap}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
      whileTap={{ scale: 0.98 }}
      data-testid="home-featured-redflag"
      className="relative w-full mb-8 rounded-[22px] px-5 py-5 text-left overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, rgba(255, 77, 109, 0.18) 0%, rgba(255, 46, 154, 0.10) 45%, rgba(20, 8, 22, 0.95) 100%)",
        border: "1.5px solid rgba(255, 77, 109, 0.55)",
        boxShadow:
          "0 0 28px rgba(255, 77, 109, 0.35), 0 16px 38px rgba(0, 0, 0, 0.5), inset 0 0 60px rgba(255, 77, 109, 0.10)",
      }}
    >
      {/* Top-right NEW badge with subtle pulse */}
      <motion.span
        className="absolute top-3 right-3 px-2.5 py-[3px] rounded-full text-[9.5px] font-black tracking-[0.18em]"
        style={{
          background: "linear-gradient(90deg, #FF2E9A 0%, #FF4D6D 100%)",
          color: "#fff",
          boxShadow: "0 0 14px rgba(255, 46, 154, 0.7)",
        }}
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
      >
        ✨ NEW
      </motion.span>

      <div className="flex items-center gap-4">
        {/* Big flag emoji + pulsing glow */}
        <div
          className="relative w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{
            background:
              "radial-gradient(circle at 50% 40%, rgba(255, 77, 109, 0.55) 0%, rgba(20, 8, 22, 0.95) 75%)",
            border: "1.5px solid rgba(255, 77, 109, 0.7)",
            boxShadow: "0 0 22px rgba(255, 77, 109, 0.65), inset 0 0 18px rgba(255, 77, 109, 0.3)",
          }}
        >
          <motion.span
            style={{ fontSize: 36, lineHeight: 1, filter: "drop-shadow(0 0 8px rgba(255, 77, 109, 0.9))" }}
            animate={{ rotate: [-4, 4, -4], y: [0, -2, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden
          >
            🚩
          </motion.span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-black tracking-[0.22em] uppercase" style={{ color: "#FF7BA1" }}>
              Most controversial pack yet
            </span>
          </div>
          <div className="text-white text-[18px] font-extrabold leading-tight">
            Red Flag Radar
          </div>
          <div className="text-[#C9C7D9] text-[12.5px] leading-snug mt-1 line-clamp-2" style={{ fontWeight: 400 }}>
            Brutally honest dating-discourse prompts. 150 ways to ask the question you're scared to ask.
          </div>
        </div>

        <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: "#FF7BA1" }} />
      </div>
    </motion.button>
  );
}
